import { FC } from 'react';
import i18next from 'i18next';
import * as Sentry from '@sentry/react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { formatTitle } from 'src/utils/helmetUtils';
import {
  Box,
  Button,
  Container,
  Typography,
  useMediaQuery,
  useTheme,
} from 'material-hu';
import errorImage from 'src/assets/unplugged.svg';
import { NAMESPACE } from './i18n';

const ErrorBoundary: FC<React.PropsWithChildren> = ({ children }) => {
  const theme = useTheme();
  const mobileDevice = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Sentry.ErrorBoundary
      onError={(err) => (window as any).newrelic.noticeError(err)}
      fallback={({ resetError }) => (
        <>
          <Helmet>
            <title>{formatTitle(i18next.t(`${NAMESPACE}:ERROR`))}</title>
          </Helmet>
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: 'background.paper',
              display: 'flex',
              minHeight: '100%',
              px: 3,
              pb: '80px',
              pt: '30px',
            }}
          >
            <Container maxWidth="lg">
              <Typography
                align="center"
                color="textPrimary"
                variant={mobileDevice ? 'h4' : 'h1'}
              >
                {`${i18next.t(`${NAMESPACE}:INFO`)}`}
              </Typography>
              <Typography
                align="center"
                color="textSecondary"
                sx={{ mt: 0.5 }}
                variant="subtitle2"
              >
                {`${i18next.t(`${NAMESPACE}:MORE_INFO`)}`}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 6,
                }}
              >
                <Box
                  alt="Under development"
                  component="img"
                  src={errorImage}
                  sx={{
                    height: 'auto',
                    maxWidth: '100%',
                    width: 400,
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 6,
                }}
              >
                <Button
                  color="primary"
                  component={RouterLink}
                  onClick={resetError}
                  to="/"
                  variant="outlined"
                >
                  {`${i18next.t(`${NAMESPACE}:GO_BACK`)}`}
                </Button>
              </Box>
            </Container>
          </Box>
        </>
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;
